import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, CheckCircle2, ChevronDown, ChevronRight, TrendingUp } from "lucide-react";

import { Button } from "@/components/ui/button";
import MietanpassungDialog from "@/components/MietanpassungDialog";
import { listWidersprueche } from "@/lib/api";
import { useSchreibschutz } from "@/lib/auth";
import { euro, germanDate } from "@/lib/format";
import { Grund } from "@/hilfe/Grund";
import { Seitenkopf } from "@/hilfe/Seitenkopf";

// Was der Bestand über sich selbst Widersprüchliches sagt — alle Prüfungen
// lesend. Die Ansicht ist absichtlich eine Arbeitsliste und kein Bericht:
// jede Zeile führt dorthin, wo sich der Widerspruch auflösen lässt.
//
// Die Zahl der Prüfungen steht nicht mehr im Text, sondern kommt aus der
// Antwort. Sie stand hier bis zum 11.10.2026 als "sieben" fest und war nach
// dem ersten Nachtrag falsch, ohne dass es jemandem aufgefallen wäre — eine
// Anzeige, die ihre eigene Grundlage behauptet statt sie zu lesen.

const TON = {
    hoch: { rand: "border-red-200", kopf: "bg-red-50", zahl: "text-red-700" },
    mittel: { rand: "border-amber-200", kopf: "bg-amber-50", zahl: "text-amber-700" },
    // Eine Pruefung, die nicht lief, ist weder gruen noch rot: sie weiss nichts.
    ausfall: { rand: "border-gray-300", kopf: "bg-gray-100", zahl: "text-gray-500" },
};

const Widersprueche = () => {
    const [daten, setDaten] = useState(null);
    const [laedt, setLaedt] = useState(true);
    const [zu, setZu] = useState(() => new Set());
    // Welche Zeile ihre Monatswerte aufgeklappt zeigt, und für wen der
    // Mietanpassungs-Dialog offen ist.
    const [offeneZeile, setOffeneZeile] = useState(null);
    const [anpassungFuer, setAnpassungFuer] = useState(null);
    const { gesperrt, grund } = useSchreibschutz();

    const laden = async () => {
        setLaedt(true);
        try {
            setDaten(await listWidersprueche());
        } catch (e) {
            toast.error("Prüfung nicht geladen", { description: e?.response?.data?.detail });
        } finally { setLaedt(false); }
    };
    useEffect(() => { laden(); }, []);

    const umschalten = (k) => setZu((alt) => {
        const n = new Set(alt);
        if (n.has(k)) n.delete(k); else n.add(k);
        return n;
    });

    // Wohin eine Zeile führt: zur Einheit, sonst zum Objekt, sonst zur Liste
    // der fehlenden Mietverhältnisse — dort ist ein Zahler ohne Vertrag zu Hause.
    const ziel = (g, z) => (z.unit_id ? `/einheiten/${z.unit_id}`
        : z.property_id ? `/immobilien/${z.property_id}`
        : g.schluessel === "zahler_ohne_vertrag" ? "/mieter/fehlend" : null);

    const gruppen = daten?.gruppen || [];
    // Eine nicht gelaufene Pruefung steht oben bei den Befunden, nicht unten
    // bei "ohne Befund": Sie hat nichts gefunden, weil sie nicht gesucht hat.
    const offen = gruppen.filter((g) => g.anzahl > 0 || g.ausgefuehrt === false);
    const sauber = gruppen.filter((g) => !g.anzahl && g.ausgefuehrt !== false);

    return (
        <div className="px-6 md:px-10 py-8 space-y-6" data-testid="widersprueche">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Prozesse</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="widersprueche-titel">
                        Widersprüche im Bestand
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        {daten?.gruppen_gesamt ? `${daten.gruppen_gesamt} Prüfungen, die` : "Prüfungen, die"} der
                        Bestand gegen sich selbst laufen lässt. Keine Schätzung, keine Schwelle — entweder
                        es passt zusammen oder nicht. Jede Zeile führt dorthin, wo sich der Widerspruch
                        auflösen lässt.
                    </p>
                    <Seitenkopf id="seite.widersprueche" />
                </div>
                <Button variant="outline" size="sm" onClick={laden} disabled={laedt} grund="Wird geladen" data-testid="widersprueche-neu">
                    <RefreshCw size={13} className={`mr-1.5 ${laedt ? "animate-spin" : ""}`} /> Neu prüfen
                </Button>
            </div>

            {daten && (
                <div className="flex items-center gap-3 text-xs text-gray-500" data-testid="widersprueche-zaehler">
                    <span><b className={daten.gesamt ? "text-red-700" : "text-emerald-700"}>{daten.gesamt}</b> Befunde</span>
                    <span>·</span>
                    <span>{daten.gruppen_offen} von {daten.gruppen_geprueft} Prüfungen schlagen an</span>
                    {/* Welche Prüfung mit welchem Fehler nicht lief, sagt die
                        Antwort je Gruppe (widersprueche(): ausgefuehrt, fehler) --
                        das ist der Grund am Hinweis (Auftrag 8, R2). */}
                    {daten.vollstaendig === false && (<><span>·</span>
                        <Grund testId="widersprueche-nicht-gelaufen"
                               text={gruppen.filter((g) => g.ausgefuehrt === false)
                                   .map((g) => (g.fehler ? `${g.titel}: ${g.fehler}` : null))}>
                            <span className="text-gray-700 font-medium inline-flex items-center gap-1">
                                <AlertTriangle size={11} className="text-gray-500" />
                                {daten.nicht_gelaufen} nicht gelaufen
                            </span>
                        </Grund></>)}
                    {daten.summe_betroffen && (<><span>·</span>
                        <span className="text-red-700 font-medium">{euro(daten.summe_betroffen)} betroffen</span></>)}
                    <span>·</span>
                    <span>Stand {germanDate(daten.stichtag)}</span>
                </div>
            )}

            {offen.map((g) => {
                const ton = TON[g.schwere] || TON.mittel;
                const auf = !zu.has(g.schluessel);
                return (
                    <div key={g.schluessel} className={`bg-white border ${ton.rand} rounded-md overflow-hidden`}
                         data-testid={`gruppe-${g.schluessel}`}>
                        <button type="button"
                                className={`w-full text-left px-5 py-3 ${ton.kopf} flex items-start gap-3 hover:brightness-95`}
                                onClick={() => umschalten(g.schluessel)}>
                            {auf ? <ChevronDown size={15} className="text-gray-400 mt-0.5 shrink-0" />
                                 : <ChevronRight size={15} className="text-gray-400 mt-0.5 shrink-0" />}
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-900 flex items-center gap-2">
                                    {g.schwere === "hoch" && <AlertTriangle size={13} className="text-red-600 shrink-0" />}
                                    {g.ausgefuehrt === false && <AlertTriangle size={13} className="text-gray-500 shrink-0" />}
                                    {g.titel}
                                    <span className={`tabular-nums ${ton.zahl}`}>
                                        {g.ausgefuehrt === false ? "—" : g.anzahl}
                                    </span>
                                    {g.summe && <span className="text-xs text-gray-500">· {euro(g.summe)}</span>}
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">{g.was_tun}</div>
                            </div>
                        </button>
                        {auf && (
                            <div className="divide-y divide-gray-100">
                                {g.zeilen.map((z, i) => {
                                    const to = ziel(g, z);
                                    // Zeilen mit Monatswerten bekommen eine eigene Darstellung:
                                    // Der Weg zur Behebung führt über den bestehenden
                                    // Mietanpassungs-Dialog, nicht über ein zweites Formular
                                    // (E42). Die Monatswerte daneben zeigen, ab wann die
                                    // Differenz läuft — und ob sie irgendwo springt.
                                    if (z.perioden?.length) {
                                        const schluessel = `${g.schluessel}-${i}`;
                                        const auf = offeneZeile === schluessel;
                                        const gleich = new Set(z.perioden.map((p) => p.differenz)).size === 1;
                                        return (
                                            <div key={i} className="px-5 py-2" data-testid={`zeile-${schluessel}`}>
                                                <div className="flex items-baseline justify-between gap-3">
                                                    <button type="button" className="text-left min-w-0 flex-1 group"
                                                            onClick={() => setOffeneZeile(auf ? null : schluessel)}>
                                                        <span className="text-sm text-slate-800 group-hover:underline">
                                                            {z.text}
                                                        </span>
                                                    </button>
                                                    {z.betrag ? (
                                                        <span className="text-sm tabular-nums text-slate-700 shrink-0">
                                                            {euro(z.betrag)}
                                                        </span>
                                                    ) : null}
                                                    {z.tenant_id && (
                                                        <Button size="sm" variant="outline" className="shrink-0 h-7 text-xs"
                                                                disabled={gesperrt} grund={grund}
                                                                hilfe="aktion.createMietanpassung"
                                                                onClick={() => setAnpassungFuer({
                                                                    id: z.tenant_id,
                                                                    full_name: (z.text || "").split("—")[0].trim(),
                                                                })}
                                                                data-testid={`btn-anpassung-${schluessel}`}>
                                                            <TrendingUp size={12} className="mr-1" /> Mietanpassung
                                                        </Button>
                                                    )}
                                                </div>
                                                {auf && (
                                                    <div className="mt-2 mb-1 ml-1 overflow-x-auto">
                                                        <table className="text-xs tabular-nums">
                                                            <thead>
                                                                <tr className="text-gray-500">
                                                                    <th className="text-left font-medium pr-4 pb-1">Monat</th>
                                                                    <th className="text-right font-medium pr-4 pb-1">Soll</th>
                                                                    <th className="text-right font-medium pr-4 pb-1">Eingang</th>
                                                                    <th className="text-right font-medium pb-1">Differenz</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {z.perioden.map((p) => (
                                                                    <tr key={p.period} className="text-slate-700">
                                                                        <td className="pr-4">{p.period}</td>
                                                                        <td className="text-right pr-4">{euro(p.soll)}</td>
                                                                        <td className="text-right pr-4">{euro(p.ist)}</td>
                                                                        <td className="text-right text-amber-700">+{euro(p.differenz)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        <div className="text-xs text-gray-500 mt-1.5 max-w-xl">
                                                            {gleich
                                                                ? "Die Differenz ist jeden Monat gleich — das spricht für eine "
                                                                  + "Mietanpassung oder eine fehlende Position (Garage, Stellplatz), "
                                                                  + "nicht für Zahlungsverhalten."
                                                                : "Die Differenz schwankt — prüfen, ob darin eine einmalige "
                                                                  + "Nachzahlung steckt oder ab wann sich der Betrag ändert."}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    const inhalt = (
                                        <div className="flex items-baseline justify-between gap-3 px-5 py-2">
                                            <span className="text-sm text-slate-800 truncate">{z.text}</span>
                                            {z.betrag ? (
                                                <span className="text-sm tabular-nums text-slate-700 shrink-0">{euro(z.betrag)}</span>
                                            ) : null}
                                        </div>
                                    );
                                    return to ? (
                                        <Link key={i} to={to} className="block hover:bg-gray-50">{inhalt}</Link>
                                    ) : <div key={i}>{inhalt}</div>;
                                })}
                            </div>
                        )}
                    </div>
                );
            })}

            {sauber.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-md p-5" data-testid="widersprueche-sauber">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-3">Ohne Befund</div>
                    <div className="space-y-1.5">
                        {sauber.map((g) => (
                            <div key={g.schluessel} className="flex items-center gap-2 text-sm text-gray-600">
                                <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                                {g.titel}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {daten && daten.gesamt === 0 && daten.vollstaendig !== false && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-6 text-center">
                    <CheckCircle2 size={20} className="text-emerald-600 mx-auto mb-2" />
                    <div className="text-sm text-emerald-900">Keine Widersprüche. Der Bestand ist in sich stimmig.</div>
                </div>
            )}

            {/* Derselbe Dialog wie an der Einheit und beim Mieter — die Wache führt
                dorthin, statt ein zweites Formular zu bauen. Nach dem Speichern
                wird neu geprüft: Stimmen Soll und Eingang danach überein,
                verschwindet die Zeile von selbst. */}
            <MietanpassungDialog
                mieter={anpassungFuer}
                open={!!anpassungFuer}
                onOpenChange={(o) => { if (!o) setAnpassungFuer(null); }}
                onDone={() => { setAnpassungFuer(null); laden(); }}
                gesperrt={gesperrt}
                grund={grund}
            />
        </div>
    );
};

export default Widersprueche;
